/**
 * Sidebar navigation config for dao-suite — kept in its own module (not in
 * AppLayout.tsx) so it is a pure, deterministically-testable function of the
 * feature flags and does NOT trip react-refresh/only-export-components.
 *
 * bl-1549 nav-href discipline: the nav config is the surface that silently
 * pointed "Membership" at the wrong route for ~7 months. Keeping it pure +
 * flag-injected lets a component test pin every href at PR time.
 */
import type React from 'react';
import {
  LayoutDashboard,
  ScrollText,
  Users,
  UserCircle,
  CalendarDays,
  Flame,
  CreditCard,
  Bell,
  Settings,
  Gift,
  Building2,
  FilePlus2,
  BadgeCheck,
} from 'lucide-react';

/** Navigation item type with optional role gating (BL-055). */
export type NavItem = {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  requiredRole?: string;
};

/** Feature-flag inputs to the nav config — injected so the nav is a pure,
 *  deterministically-testable function of the flags (bl-1549 nav-href discipline). */
export interface NavFlags {
  propertyGraduation: boolean;
  submissionCapture: boolean;
  credentialStatus: boolean;
}

/** AC1: Navigation items — icon + label + route. */
/** BL-055 AC2/AC3: Proposals and Burn & Donate require member role. */
export function buildNavigation(flags: NavFlags): NavItem[] {
  return [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Proposals', href: '/proposals', icon: ScrollText, requiredRole: 'member' },
    // ae-001-4 — ADR-043 member submission capture wizard. Feature-flag-gated OFF
    // by default (scope-stop, AI-P4-04): the envelope-ingest endpoint is not built
    // yet, so the entry is hidden until VITE_ENABLE_SUBMISSION_CAPTURE=true. When
    // off the nav is identical to before (the AppLayout pinned-label regression is
    // unaffected). The /submit route is correspondingly flag-gated in App.tsx.
    ...(flags.submissionCapture
      ? [{ name: 'Submit', href: '/submit', icon: FilePlus2, requiredRole: 'member' } as NavItem]
      : []),
    { name: 'Members', href: '/members', icon: Users },
    { name: 'My Profile', href: '/profile', icon: UserCircle },
    { name: 'Events', href: '/events', icon: CalendarDays },
    // bl-1585: 'Blog' (/blog) is intentionally NOT in the member nav for the DAO alpha
    // (decision C). The /blog routes stay mounted in App.tsx (deep-link/E2E reach them);
    // the in-app member reader/comments/editor are deferred. Re-add the entry to reinstate.
    { name: 'Claim Airdrop', href: '/claim', icon: Gift, requiredRole: 'member' },
    // ae-001-6 — ADR-043 credential-status surface. Flag-gated OFF by default
    // (scope-stop): the surface reads the live attestation-record canister and is
    // fail-closed, but stays hidden until the alpha lights it up. When off the nav
    // is identical to before (the AppLayout pinned-label regression is
    // unaffected). The /credentials route is correspondingly flag-gated in App.tsx.
    ...(flags.credentialStatus
      ? [
          {
            name: 'Credentials',
            href: '/credentials',
            icon: BadgeCheck,
            requiredRole: 'member',
          } as NavItem,
        ]
      : []),
    // walkable-dao-001-9 — member-facing property status → proposal graduation.
    // Feature-flag-gated OFF by default (backend owner-scoped registry read edge not
    // yet wired); when off the nav is identical to before, so the pinned-label
    // regression below is unaffected.
    ...(flags.propertyGraduation
      ? [
          {
            name: 'My Properties',
            href: '/property-status',
            icon: Building2,
            requiredRole: 'member',
          } as NavItem,
        ]
      : []),
    { name: 'Burn & Donate', href: '/burn-donation', icon: Flame, requiredRole: 'member' },
    // bl-1549/bl-1565: MUST point at /membership (the state-branching front door:
    // none→join, Registered→KYC stepper, Active/Expired→contextual renew links),
    // NOT /membership/renewal (renewal-only, window-gated, no KYC). Pointing here
    // at /renewal made a new/registered member clicking "Membership" hit the
    // closed-window guard (bl-1565) and never reach the Didit KYC surface
    // (bl-1549). /membership/renewal stays reachable via the in-context "Renew"
    // links on /membership. Guarded by AppLayout.test.tsx.
    { name: 'Membership', href: '/membership', icon: CreditCard },
    { name: 'Notifications', href: '/notifications', icon: Bell },
    { name: 'Settings', href: '/settings', icon: Settings },
  ];
}
